import { getDadDisplayName, type MissionType, type KidPolicy } from '../types';
import { getCurrentProfile } from '../services/authService';
import { createMission } from '../services/missionService';
import { showToast } from '../components/Toast';
import { isValidZip } from '../utils/zipUtils';

export function renderCreateMissionModal(): string {
  const profile = getCurrentProfile();
  const defaultZip = profile?.neighborhood?.match(/\b\d{5}\b/)?.[0] || '';

  return `
    <div id="modal-create-mission" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto modal-backdrop animate-fade-in font-sans">
      <div class="bg-[#0a0c13] border border-white/[0.12] rounded-2xl max-w-2xl w-full p-5 sm:p-6 shadow-[0_15px_40px_rgba(0,0,0,0.9)] space-y-4 my-8 max-h-[90vh] overflow-y-auto relative">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-white/[0.08] pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-10 h-10 rounded-xl bg-transparent border border-cyan-400/60 text-cyan-400 flex items-center justify-center font-bold text-lg shadow-[0_0_10px_rgba(0,240,255,0.25)]">
              <i class="fa-solid fa-bullhorn"></i>
            </div>
            <div>
              <h3 class="text-base font-black text-slate-100">Rally Dads &bull; Create Event</h3>
              <p class="text-xs text-slate-400">Post a project, organize a social, or lead community service</p>
            </div>
          </div>
          <button id="close-create-modal-btn" class="text-slate-400 hover:text-white p-1 cursor-pointer">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <form id="create-mission-form" class="space-y-4 text-xs">
          
          <!-- Archetype Selection -->
          <div class="space-y-1.5">
            <label class="block font-bold text-slate-300 uppercase tracking-wider">Mission Archetype:</label>
            <div class="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <label class="flex flex-col items-center justify-center p-3 rounded-xl border border-white/[0.1] bg-transparent cursor-pointer hover:border-amber-400/60 has-[:checked]:border-amber-400 has-[:checked]:shadow-[0_0_12px_rgba(255,183,3,0.3)] transition-all">
                <input type="radio" name="new-mission-type" value="project" checked class="hidden">
                <i class="fa-solid fa-hammer text-amber-400 text-lg mb-1"></i>
                <span class="font-bold text-slate-200 text-xs">Project</span>
                <span class="text-[10px] text-slate-400 text-center">Hands-on DIY</span>
              </label>
              
              <label class="flex flex-col items-center justify-center p-3 rounded-xl border border-white/[0.1] bg-transparent cursor-pointer hover:border-cyan-400/60 has-[:checked]:border-cyan-400 has-[:checked]:shadow-[0_0_12px_rgba(0,240,255,0.3)] transition-all">
                <input type="radio" name="new-mission-type" value="social" class="hidden">
                <i class="fa-solid fa-burger text-cyan-400 text-lg mb-1"></i>
                <span class="font-bold text-slate-200 text-xs">Social</span>
                <span class="text-[10px] text-slate-400 text-center">BBQ & hang</span>
              </label>

              <label class="flex flex-col items-center justify-center p-3 rounded-xl border border-white/[0.1] bg-transparent cursor-pointer hover:border-emerald-400/60 has-[:checked]:border-emerald-400 has-[:checked]:shadow-[0_0_12px_rgba(0,255,136,0.3)] transition-all">
                <input type="radio" name="new-mission-type" value="volunteer" class="hidden">
                <i class="fa-solid fa-handshake-angle text-emerald-400 text-lg mb-1"></i>
                <span class="font-bold text-slate-200 text-xs">Volunteer</span>
                <span class="text-[10px] text-slate-400 text-center">Civic service</span>
              </label>

              <label class="flex flex-col items-center justify-center p-3 rounded-xl border border-white/[0.1] bg-transparent cursor-pointer hover:border-sky-400/60 has-[:checked]:border-sky-400 has-[:checked]:shadow-[0_0_12px_rgba(56,189,248,0.3)] transition-all">
                <input type="radio" name="new-mission-type" value="sports" class="hidden">
                <i class="fa-solid fa-basketball text-sky-400 text-lg mb-1"></i>
                <span class="font-bold text-slate-200 text-xs">Sports</span>
                <span class="text-[10px] text-slate-400 text-center">Pickup games</span>
              </label>

              <label class="flex flex-col items-center justify-center p-3 rounded-xl border border-white/[0.1] bg-transparent cursor-pointer hover:border-purple-400/60 has-[:checked]:border-purple-400 has-[:checked]:shadow-[0_0_12px_rgba(168,85,247,0.3)] transition-all">
                <input type="radio" name="new-mission-type" value="gaming" class="hidden">
                <i class="fa-solid fa-gamepad text-purple-400 text-lg mb-1"></i>
                <span class="font-bold text-slate-200 text-xs">Gaming</span>
                <span class="text-[10px] text-slate-400 text-center">Tabletop & video</span>
              </label>
            </div>
          </div>

          <!-- Title -->
          <div class="space-y-1">
            <label class="block font-bold text-slate-300">Mission Title / Objective <span class="text-cyan-400">*</span></label>
            <input type="text" id="new-mission-title" required placeholder="e.g. Assembling 10x12 Utility Shed / Weekend Smoker Cookout" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)]">
          </div>

          <!-- Description (200 word max with live counter) -->
          <div class="space-y-1">
            <div class="flex items-center justify-between">
              <label class="block font-bold text-slate-300">
                Description / Scope of Work <span class="text-cyan-400">*</span>
              </label>
              <span id="desc-word-counter" class="text-[11px] font-semibold text-slate-400">0 / 200 words</span>
            </div>
            <textarea 
              id="new-mission-desc" 
              required 
              rows="3" 
              placeholder="Explain the project or hangout, what materials are on site, and what attendees can expect (200 words max)..." 
              class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(0,240,255,0.2)] leading-relaxed resize-none"
            ></textarea>
            <p id="desc-word-error" class="text-[10px] text-pink-400 hidden font-bold">Description exceeds the 200-word limit. Please shorten your description.</p>
          </div>

          <!-- Date & Tentative Options -->
          <div class="p-3.5 rounded-xl bg-transparent border border-white/[0.08] space-y-3">
            <div class="flex items-center justify-between">
              <label class="font-bold text-slate-300 flex items-center gap-2">
                <i class="fa-regular fa-calendar text-cyan-400"></i> Date & Time <span class="text-cyan-400">*</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer text-slate-300 text-xs hover:text-cyan-300">
                <input type="checkbox" id="tentative-date-check" class="rounded border-white/20 text-cyan-500 focus:ring-0 bg-[#06070a]">
                <span class="font-medium">Tentative (offer multiple dates)</span>
              </label>
            </div>

            <!-- Single Date View -->
            <div id="single-date-container" class="space-y-1">
              <input 
                type="text" 
                id="new-mission-datetime" 
                placeholder="e.g. Saturday, Oct 12 &bull; 9:00 AM - 1:00 PM" 
                class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-cyan-400"
              >
            </div>

            <!-- Tentative Multiple Dates View (Hidden by default) -->
            <div id="tentative-dates-container" class="hidden space-y-2">
              <p class="text-[11px] text-slate-400">
                Provide 2 or more candidate dates so interested dads can RSVP with their availability:
              </p>
              <div id="candidate-dates-list" class="space-y-2">
                <div class="flex items-center gap-2 candidate-date-row">
                  <input type="text" placeholder="Candidate Option 1: e.g. Saturday, Oct 12 &bull; 10:00 AM" class="candidate-date-input flex-1 bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-1.5 text-slate-200 focus:border-cyan-400">
                </div>
                <div class="flex items-center gap-2 candidate-date-row">
                  <input type="text" placeholder="Candidate Option 2: e.g. Sunday, Oct 13 &bull; 1:00 PM" class="candidate-date-input flex-1 bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-1.5 text-slate-200 focus:border-cyan-400">
                </div>
              </div>
              <button type="button" id="add-candidate-date-btn" class="text-xs font-semibold text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5 cursor-pointer pt-1">
                <i class="fa-solid fa-plus text-[10px]"></i> Add another tentative date
              </button>
            </div>
          </div>

          <!-- Location (Address fields optional, ZIP Code required) -->
          <div class="p-3.5 rounded-xl bg-transparent border border-white/[0.08] space-y-2.5">
            <div class="flex items-center justify-between">
              <label class="font-bold text-slate-300 flex items-center gap-2">
                <i class="fa-solid fa-location-dot text-cyan-400"></i> Location & Privacy Guardrail
              </label>
              <span class="text-[10px] text-slate-400">Street revealed to enlisted dads only</span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div class="space-y-1">
                <label class="block text-[11px] text-slate-400">Street Address <span class="text-slate-500">(Optional)</span></label>
                <input type="text" id="new-mission-street" placeholder="e.g. 1428 Maple Ridge Rd" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-400">
              </div>

              <div class="space-y-1">
                <label class="block text-[11px] text-slate-400">City <span class="text-slate-500">(Optional)</span></label>
                <input type="text" id="new-mission-city" placeholder="e.g. Columbus" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-400">
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div class="space-y-1">
                <label class="block text-[11px] text-slate-400">State <span class="text-slate-500">(Optional)</span></label>
                <input type="text" id="new-mission-state" placeholder="e.g. OH" maxlength="20" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-400">
              </div>

              <div class="space-y-1">
                <label class="block text-[11px] font-bold text-cyan-300 flex items-center justify-between">
                  <span>5-Digit ZIP Code <span class="text-cyan-400">* Required</span></span>
                  <span class="text-[10px] font-normal text-slate-400">Used for distance search</span>
                </label>
                <input 
                  type="text" 
                  id="new-mission-zip" 
                  required 
                  maxlength="5" 
                  pattern="[0-9]{5}" 
                  value="${defaultZip}"
                  placeholder="e.g. 43215" 
                  class="w-full bg-[#06070a] border border-cyan-400/50 rounded-lg px-3 py-2 text-slate-100 font-bold focus:border-cyan-400 focus:shadow-[0_0_8px_rgba(0,240,255,0.3)]"
                >
              </div>
            </div>

            <div class="space-y-1 pt-1">
              <label class="block text-[11px] text-slate-400">General Area / Neighborhood <span class="text-slate-500">(Shown publicly on feed)</span></label>
              <input type="text" id="new-mission-masked-loc" placeholder="e.g. North Suburb (Near Oak Park)" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-400">
            </div>
          </div>

          <!-- Kid Policy & Provisions -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="space-y-1">
              <label class="block font-bold text-slate-300">Kid Policy</label>
              <select id="new-mission-kid-policy" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-2.5 py-2 text-slate-300 focus:outline-none focus:border-cyan-400">
                <option value="dads_only">Dads Only (Power Tools / Adult Focus)</option>
                <option value="kids_welcome">Kids Welcome (Yard Play / Activities)</option>
                <option value="family_all">Entire Family Gathering (Family Friendly)</option>
              </select>
            </div>
            <div class="space-y-1">
              <label class="block font-bold text-slate-300">Provisions on Host</label>
              <input type="text" id="new-mission-provisions" placeholder="e.g. Cold drinks, burgers & iced tea" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-400">
            </div>
          </div>

          <!-- Skills & Tools Wanted -->
          <div class="space-y-1">
            <label class="block font-bold text-slate-300">Skills / Tools Wanted (comma-separated)</label>
            <input type="text" id="new-mission-skills" placeholder="e.g. 2 Framers, Post hole digger, Impact driver, General lifting" class="w-full bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-2 text-slate-200 focus:border-cyan-400">
          </div>

          <!-- MANDATORY HOST PERMIT & SAFETY CONFIRMATION CHECKBOX -->
          <div class="p-3.5 bg-transparent border border-amber-500/50 rounded-xl space-y-2 shadow-[0_0_10px_rgba(255,183,3,0.1)]">
            <div class="flex items-center gap-1.5 text-amber-300 font-bold">
              <i class="fa-solid fa-stamp"></i> Mandatory Host Responsibility Confirmation
            </div>
            <label class="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" id="host-responsibility-check" class="mt-1 h-4 w-4 rounded border-white/20 text-cyan-500 focus:ring-0 bg-[#06070a]">
              <span class="text-[11px] text-slate-300 leading-snug">
                I certify that I own or have lawful authority over the project premises. I bear <strong>sole responsibility for obtaining any necessary municipal permits, HOA approvals, and code compliance</strong>. I am solely responsible for job site safety, providing or requiring safety equipment (PPE), and agree that Pops Culture assumes zero responsibility or liability for project engineering, tools, or accidents.
              </span>
            </label>
          </div>

          <!-- Submit Buttons -->
          <div class="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.08]">
            <button type="button" id="cancel-create-btn" class="px-4 py-2 rounded-lg text-slate-400 hover:text-white border border-transparent hover:border-white/10 transition-colors cursor-pointer">
              Cancel
            </button>
            <button type="submit" id="post-mission-submit-btn" class="btn-primary px-5 py-2.5 rounded-xl flex items-center gap-2 font-black cursor-pointer">
              <i class="fa-solid fa-bullhorn"></i> Post Mission
            </button>
          </div>

        </form>

      </div>
    </div>
  `;
}

export function bindCreateMissionModalEvents(
  onSuccess: () => void,
  onCancel: () => void
): void {
  document.getElementById('close-create-modal-btn')?.addEventListener('click', onCancel);
  document.getElementById('cancel-create-btn')?.addEventListener('click', onCancel);

  // Description word count listener
  const descEl = document.getElementById('new-mission-desc') as HTMLTextAreaElement;
  const wordCounterEl = document.getElementById('desc-word-counter');
  const wordErrorEl = document.getElementById('desc-word-error');

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  descEl?.addEventListener('input', () => {
    const words = countWords(descEl.value);
    if (wordCounterEl) {
      wordCounterEl.textContent = `${words} / 200 words`;
      if (words > 200) {
        wordCounterEl.classList.add('text-pink-400');
        wordCounterEl.classList.remove('text-slate-400');
        wordErrorEl?.classList.remove('hidden');
      } else {
        wordCounterEl.classList.remove('text-pink-400');
        wordCounterEl.classList.add('text-slate-400');
        wordErrorEl?.classList.add('hidden');
      }
    }
  });

  // Tentative checkbox toggle
  const tentativeCheck = document.getElementById('tentative-date-check') as HTMLInputElement;
  const singleDateContainer = document.getElementById('single-date-container');
  const tentativeDatesContainer = document.getElementById('tentative-dates-container');
  const singleDateInput = document.getElementById('new-mission-datetime') as HTMLInputElement;

  tentativeCheck?.addEventListener('change', () => {
    if (tentativeCheck.checked) {
      singleDateContainer?.classList.add('hidden');
      tentativeDatesContainer?.classList.remove('hidden');
      singleDateInput.required = false;
    } else {
      singleDateContainer?.classList.remove('hidden');
      tentativeDatesContainer?.classList.add('hidden');
      singleDateInput.required = true;
    }
  });

  // Add candidate date row button
  const addDateBtn = document.getElementById('add-candidate-date-btn');
  const candidateDatesList = document.getElementById('candidate-dates-list');

  addDateBtn?.addEventListener('click', () => {
    if (!candidateDatesList) return;
    const row = document.createElement('div');
    row.className = 'flex items-center gap-2 candidate-date-row';
    const count = candidateDatesList.querySelectorAll('.candidate-date-row').length + 1;
    row.innerHTML = `
      <input type="text" placeholder="Candidate Option ${count}: e.g. Saturday, Oct 19 &bull; 9:00 AM" class="candidate-date-input flex-1 bg-[#06070a] border border-white/[0.1] rounded-lg px-3 py-1.5 text-slate-200 focus:border-cyan-400">
      <button type="button" class="remove-candidate-date-btn text-slate-500 hover:text-pink-400 p-1 cursor-pointer">
        <i class="fa-solid fa-xmark"></i>
      </button>
    `;
    candidateDatesList.appendChild(row);

    row.querySelector('.remove-candidate-date-btn')?.addEventListener('click', () => {
      row.remove();
    });
  });

  // Form submit
  const form = document.getElementById('create-mission-form') as HTMLFormElement;
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const hostCheck = document.getElementById('host-responsibility-check') as HTMLInputElement;
    if (!hostCheck?.checked) {
      showToast('Host must confirm permit, safety, and property authority.', 'warning');
      return;
    }

    const description = descEl.value.trim();
    const wordCount = countWords(description);
    if (wordCount === 0) {
      showToast('Please enter an event description.', 'warning');
      return;
    }
    if (wordCount > 200) {
      showToast(`Description exceeds the 200-word limit (${wordCount}/200 words).`, 'warning');
      return;
    }

    const zipCode = (document.getElementById('new-mission-zip') as HTMLInputElement).value.trim();
    if (!isValidZip(zipCode)) {
      showToast('Please provide a valid 5-digit US ZIP Code.', 'warning');
      return;
    }

    const isTentative = Boolean(tentativeCheck?.checked);
    let datetime = '';
    let tentativeDates: string[] = [];

    if (isTentative) {
      const inputs = document.querySelectorAll('.candidate-date-input') as NodeListOf<HTMLInputElement>;
      tentativeDates = Array.from(inputs).map(inp => inp.value.trim()).filter(Boolean);
      if (tentativeDates.length === 0) {
        showToast('Please provide at least one tentative date option.', 'warning');
        return;
      }
      datetime = `Tentative: ${tentativeDates[0]} (${tentativeDates.length} options)`;
    } else {
      datetime = singleDateInput.value.trim();
      if (!datetime) {
        showToast('Please enter a date and time for the event.', 'warning');
        return;
      }
    }

    const typeRadio = document.querySelector('input[name="new-mission-type"]:checked') as HTMLInputElement;
    const type = (typeRadio?.value || 'project') as MissionType;
    const title = (document.getElementById('new-mission-title') as HTMLInputElement).value.trim();
    const street = (document.getElementById('new-mission-street') as HTMLInputElement).value.trim();
    const city = (document.getElementById('new-mission-city') as HTMLInputElement).value.trim();
    const state = (document.getElementById('new-mission-state') as HTMLInputElement).value.trim();
    let locationMasked = (document.getElementById('new-mission-masked-loc') as HTMLInputElement).value.trim();

    if (!locationMasked) {
      locationMasked = [city, state, zipCode].filter(Boolean).join(', ') || `Area ZIP ${zipCode}`;
    }

    const exactAddress = [street, city, state, zipCode].filter(Boolean).join(', ') || `ZIP ${zipCode}`;
    const kidPolicy = (document.getElementById('new-mission-kid-policy') as HTMLSelectElement).value as KidPolicy;
    const provisions = (document.getElementById('new-mission-provisions') as HTMLInputElement).value.trim();
    const skillsRaw = (document.getElementById('new-mission-skills') as HTMLInputElement).value.trim();
    const skillsNeeded = skillsRaw ? skillsRaw.split(',').map(s => s.trim()).filter(Boolean) : ['General hands'];

    const kidPolicyTextMap: Record<KidPolicy, string> = {
      dads_only: 'Power Tools Present — Dads Only',
      kids_welcome: 'Kids Welcome (Yard Play / Activities)',
      family_all: 'Entire Family Gathering (Family Friendly)'
    };

    const profile = getCurrentProfile();
    if (!profile) {
      showToast('Please sign in to post an event.', 'warning');
      return;
    }

    const submitBtn = document.getElementById('post-mission-submit-btn') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Posting...';
    }

    try {
      await createMission({
        type,
        title,
        description,
        datetime,
        isTentative,
        tentativeDates,
        street,
        city,
        state,
        zipCode,
        locationMasked,
        exactAddress,
        kidPolicy,
        kidPolicyText: kidPolicyTextMap[kidPolicy] || 'Kids Welcome',
        provisions: provisions || 'Host providing cold drinks & snacks',
        skillsNeeded,
        toolsNeeded: ['Basic hand tools'],
        host: {
          userId: profile.uid,
          name: getDadDisplayName(profile),
          avatarInitials: profile.avatarInitials,
          badgesCount: profile.badgesEarned.length,
          yearsDad: profile.yearsAsDad
        },
        maxSlots: 6,
        status: 'active'
      }, true);

      showToast('Event created successfully!');
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create event.';
      showToast(msg, 'warning');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="fa-solid fa-bullhorn"></i> Post Mission';
      }
    }
  });

  const modal = document.getElementById('modal-create-mission');
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) {
      onCancel();
    }
  });
}
